import { useContext, type ChangeEvent } from 'react'

import {
  Button,
  Form,
  ModalContext,
  SectionDivider,
  SidebarModal,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useManageInvoice from '@hooks/useManageInvoice'
import useInvoice from '@hooks/useInvoice'

import EntitySelector from '@components/EntitySelector'

const MODAL_KEY = 'ManageInvoiceDebtorModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateInvoice: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageInvoiceDebtorModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateInvoice } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const invoice = modalPayload.invoice || null

  const invoicePayload = useInvoice(invoice)
  const {
    callbacks: { updateInvoice: updateFn },
    updating,
  } = invoicePayload

  const adminInvoicePayload = useManageInvoice({ invoice })
  const {
    callbacks: {
      getEntityString,
      handleEntitySelectorChange,
      handleEntityStateChange,
    },
    invoiceFormPayload,
    isAdmin,
    isProviderDirector,
    provider,
  } = adminInvoicePayload

  const {
    entityState,
    entityState: {
      address, debtor_id, debtor_name, debtor_type, purchase_order,
    },
    handlers,
    saveEnabled,
  } = invoiceFormPayload

  const updateInvoicePayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: {
      ...entityState,
    },
    shouldRedirect: true,
    toastText: 'Debtor Details Updated',
  }

  return (
    <SidebarModal
      callbacks={callbacks}
      clickSafeZone
      modalKey={MODAL_KEY}
      showModal={showModal}
    >
      <SidebarModal.Header
        callbacks={callbacks}
        title="Manage"
        titleSecondLine="Debtor"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Debtor Type">
            <select
              onChange={(e: ChangeEvent<HTMLSelectElement>) => handleEntityStateChange(e)}
              value={debtor_type}
            >
              <option value="">Please Select...</option>
              <option value="Agency">Agency</option>
              {!isProviderDirector && <option value="Provider">Provider</option>}
              {isProviderDirector && <option value="Provider">{provider?.title}</option>}
            </select>
          </Form.Field>

          {(isAdmin || debtor_type === 'Agency') && (
            <EntitySelector
              callbacks={{
                // @ts-expect-error TS2719
                selectItem: entity => handleEntitySelectorChange({ entity, editing: true }),
              }}
              entityKey={getEntityString(debtor_type)}
              label="Debtor Entity"
              marginTop="large"
              selectedItemId={debtor_id}
              searchRequestOptions={{ billable: true }}
            />
          )}
        </Form>

        <Form.Field label="Debtor Name" marginTop="large">
          <input name="debtor_name" type="text" value={debtor_name} {...handlers} />
        </Form.Field>

        <Form.Field label="Purchase Order #" marginTop="large">
          <input name="purchase_order" type="text" value={purchase_order} {...handlers} />
        </Form.Field>

        <SectionDivider marginTop="large">DEBTOR ADDRESS</SectionDivider>

        <Form.Field label="Street Address" marginTop="large">
          {/*
           // @ts-expect-error TS2322 */}
          <textarea name="address" style={{ height: 80, resize: 'vertical' }} value={address} {...handlers} />
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          loadingBubbleColor="white"
          onClick={() => updateInvoice(updateInvoicePayload)}
          size="large"
        >
          {saveEnabled ? 'Save Debtor Details' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageInvoiceDebtorModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
